import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, User, MapPin, CreditCard, Calendar, Check, Loader2, AlertCircle, Shield } from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';
import PlanComparisonTable from './PlanComparisonTable';
import TermsModal from './TermsModal';
import {
  detectBrandFromHost,
  getBrandInfo,
  getPlanComparisonDataForBrand,
  getPlansForBrand,
} from '@/lib/brand-utils';
import { resolveStaxWebPaymentsToken, useStax } from '@/hooks/useStax';
import { calculateDiscount, validateDiscountCode } from '@/lib/stax';
import { initMembershipTracking, trackBeginCheckout, trackEvent, trackPurchase } from '@/lib/membership-tracking';
import { getMembershipTermsVersion } from '@/lib/membership-terms';

// API Base URL
// - In dev: default to the local API (so we don’t accidentally call production).
// - Override explicitly via VITE_API_URL when needed.
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV
    ? 'http://localhost:3004'
    : import.meta.env.VITE_MARKETING_API_URL) ||
  'http://localhost:3004';

// NOTE: StaxJs is declared globally in the shared useStax hook.

interface CustomerData {
  phone: string;
  isMobile: boolean;
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
  serviceAccountId?: string;
  customerId?: string;
  locationId?: string;
  existingCustomer: boolean;
}

interface MembershipSelection {
  planId: string;
  planCode: string;
  planName: string;
  visits: 1 | 2;
  systems: number;
  billingFrequency: 'monthly' | 'annual';
  price: number;
}

type PublicConfigMembershipPlan = {
  id: string;
  company_id: string;
  plan_code: string;
  plan_name: string;
  description?: string | null;
  features?: any[] | null;
  coverage_details?: any[] | null;
  sort_order?: number | null;
  is_active?: boolean | null;
  base_price_monthly_1_pm: number;
  base_price_monthly_2_pm: number;
  base_price_annual_1_pm: number;
  base_price_annual_2_pm: number;
  additional_system_monthly_1_pm: number;
  additional_system_monthly_2_pm: number;
  additional_system_annual_1_pm: number;
  additional_system_annual_2_pm: number;
};

type PlanPricingMatrix = Pick<
  PublicConfigMembershipPlan,
  | 'base_price_monthly_1_pm'
  | 'base_price_monthly_2_pm'
  | 'base_price_annual_1_pm'
  | 'base_price_annual_2_pm'
  | 'additional_system_monthly_1_pm'
  | 'additional_system_monthly_2_pm'
  | 'additional_system_annual_1_pm'
  | 'additional_system_annual_2_pm'
>;

interface EnrollmentPlanCard {
  id: string;
  code: string;
  name: string;
  color: string;
  popular?: boolean;
  description?: string;
  features: string[];
  basePrice: number;
  pricing?: PlanPricingMatrix;
  annualOnly?: boolean;
}

interface ScheduleSelection {
  wantsScheduling: boolean;
  date?: string;
  timeSlot?: '8am-12pm' | '12pm-4pm';
  equipmentAge?: '8_or_more' | 'under_8' | 'not_sure';
}

type Step =
  | 'phone'
  | 'customer-select'
  | 'customer-info'
  | 'plan-selection'
  | 'payment'
  | 'scheduling'
  | 'complete';

interface Props {
  onComplete?: () => void;
  initialPlanId?: string;
  initialPhone?: string;
  autoSubmitInitialPhone?: boolean;
  initialSystems?: number;
  initialBillingFrequency?: 'monthly' | 'annual';
}

export default function MembershipEnrollmentWizard({
  onComplete,
  initialPlanId,
  initialPhone,
  autoSubmitInitialPhone,
  initialSystems,
  initialBillingFrequency,
}: Props) {
  const brandKey = detectBrandFromHost();
  const brandInfo = getBrandInfo(brandKey);
  const brandPlans = getPlansForBrand(brandKey);
  const { companyId } = brandInfo;
  const [step, setStep] = useState<Step>('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    initMembershipTracking();
  }, []);

  const autoSubmittedInitialPhoneRef = useRef(false);
  const leadNotifyAttemptedRef = useRef(false);
  const phoneLeadNotifyTimeoutRef = useRef<number | null>(null);
  const phoneLeadNotifyLastPhoneRef = useRef<string>('');

  // Keep the wizard centered/visible as the user progresses.
  const wizardScrollRef = useRef<HTMLDivElement | null>(null);
  const wizardScrollDidMountRef = useRef(false);
  useEffect(() => {
    if (!wizardScrollDidMountRef.current) {
      wizardScrollDidMountRef.current = true;
      return;
    }

    wizardScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [step]);

  const [customerMatches, setCustomerMatches] = useState<CustomerData[] | null>(null);
  
  const [customerData, setCustomerData] = useState<CustomerData>({
    phone: '',
    isMobile: true,
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    address2: '',
    city: '',
    state: 'FL',
    zipCode: '',
    existingCustomer: false,
  });

  const [technicianAttribution, setTechnicianAttribution] = useState({
    name: '',
    email: '',
  });

  // Lead capture: notify the office as soon as a valid phone number is entered.
  // Best-effort only: should never block the enrollment UX.
  useEffect(() => {
    if (step !== 'phone') return;

    let cleanedPhone = String(customerData.phone || '').replace(/\D/g, '');
    if (cleanedPhone.length === 11 && cleanedPhone.startsWith('1')) {
      cleanedPhone = cleanedPhone.slice(1);
    }

    if (cleanedPhone.length < 10) return;

    if (phoneLeadNotifyTimeoutRef.current) {
      clearTimeout(phoneLeadNotifyTimeoutRef.current);
      phoneLeadNotifyTimeoutRef.current = null;
    }

    phoneLeadNotifyTimeoutRef.current = window.setTimeout(() => {
      phoneLeadNotifyTimeoutRef.current = null;

      // Avoid spamming if the user toggles between steps or re-types the same number.
      if (phoneLeadNotifyLastPhoneRef.current === cleanedPhone) return;
      phoneLeadNotifyLastPhoneRef.current = cleanedPhone;

      fetch(`${API_BASE_URL}/api/memberships/lead-notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId,
          lead: {
            phone: cleanedPhone,
            source: 'wizard_phone',
            domain: window.location.hostname,
          },
        }),
      }).catch((leadErr) => {
        console.warn('lead-notify (phone) error', leadErr);
      });
    }, 800);

    return () => {
      if (phoneLeadNotifyTimeoutRef.current) {
        clearTimeout(phoneLeadNotifyTimeoutRef.current);
        phoneLeadNotifyTimeoutRef.current = null;
      }
    };
  }, [step, companyId, customerData.phone]);

  const [membershipSelection, setMembershipSelection] = useState<MembershipSelection | null>(null);
  const [scheduleSelection, setScheduleSelection] = useState<ScheduleSelection>({
    wantsScheduling: false,
  });

  const [enrollmentResult, setEnrollmentResult] = useState<
    { membershipId: string; nextBillingDate?: string } | null
  >(null);
  const [scheduledVisit, setScheduledVisit] = useState<any | null>(null);
  const [confirmationEmail, setConfirmationEmail] = useState<
    { attempted: boolean; sent: boolean; message?: string } | null
  >(null);

  const [discountCodes, setDiscountCodes] = useState<any[]>([]);
  const [discountCodesLoading, setDiscountCodesLoading] = useState(false);
  const [membershipPlans, setMembershipPlans] = useState<PublicConfigMembershipPlan[]>([]);
  const [promoCode, setPromoCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<any | null>(null);

  // Prefill the promo field from a ?promo= URL param (e.g. links from brand sites: ?promo=NEW26).
  // Validation/auto-apply happens in the payment step once discount codes are loaded.
  useEffect(() => {
    try {
      const urlPromo = new URLSearchParams(window.location.search).get('promo');
      if (urlPromo && urlPromo.trim()) setPromoCode(urlPromo.trim().toUpperCase());
    } catch {
      // ignore malformed query strings
    }
  }, []);

  // DEV-only: allow jumping directly to later steps for UX verification without completing payment.
  // Example: http://localhost:3001/plans?debugStep=scheduling
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const debugStep = new URLSearchParams(window.location.search).get('debugStep');
    if (debugStep !== 'plan-selection' && debugStep !== 'payment' && debugStep !== 'scheduling') return;

    // Prevent any phone auto-submit logic from racing this override.
    autoSubmittedInitialPhoneRef.current = true;

    setCustomerData((prev) => ({
      ...prev,
      phone: prev.phone || '(561) 555-0101',
      isMobile: true,
      firstName: prev.firstName || 'Debug',
      lastName: prev.lastName || 'User',
      email: prev.email || 'debug@example.com',
      address: prev.address || '123 Main St',
      city: prev.city || 'West Palm Beach',
      state: prev.state || 'FL',
      zipCode: prev.zipCode || '33401',
      existingCustomer: true,
    }));

    if (debugStep === 'plan-selection') {
      setStep('plan-selection');
      return;
    }

    setMembershipSelection((prev) => {
      if (prev) return prev;
      const defaultPlanId = initialPlanId || brandPlans?.[0]?.id || `${brandInfo.companyId}-standard`;
      return {
        planId: defaultPlanId,
        planCode: defaultPlanId,
        planName: 'Debug Plan',
        visits: 1,
        systems: 1,
        billingFrequency: 'monthly',
        price: 0,
      };
    });

    setEnrollmentResult({ membershipId: 'debug-membership-id' });
    setScheduleSelection({ wantsScheduling: true });
    setStep(debugStep === 'payment' ? 'payment' : 'scheduling');
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadDiscountCodes = async () => {
      setDiscountCodesLoading(true);
      try {
        const domain = window.location.hostname;
        const response = await fetch(
          `${API_BASE_URL}/api/memberships/public-config?domain=${encodeURIComponent(domain)}`
        );

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          const message = data?.message || data?.error || 'Failed to load membership configuration';
          throw new Error(message);
        }

        if (!cancelled) {
          setDiscountCodes(Array.isArray(data?.discount_codes) ? data.discount_codes : []);
          setMembershipPlans(Array.isArray(data?.membership_plans) ? data.membership_plans : []);
        }
      } catch (err) {
        console.error('Failed to load discount codes:', err);
      } finally {
        if (!cancelled) setDiscountCodesLoading(false);
      }
    };

    loadDiscountCodes();

    return () => {
      cancelled = true;
    };
  }, []);

  const enrollmentPlans: EnrollmentPlanCard[] = React.useMemo(() => {
    const palette = [brandInfo.colors.primary, brandInfo.colors.secondary, brandInfo.colors.accent];

    const normalizeFeatures = (raw: any[] | null | undefined) => {
      if (!Array.isArray(raw)) return [];
      return raw
        .map((feature) => (typeof feature === 'string' ? feature : feature?.text))
        .filter((feature): feature is string => Boolean(feature));
    };

    if (Array.isArray(membershipPlans) && membershipPlans.length > 0) {
      const activePlans = membershipPlans
        .filter((plan) => plan && plan.is_active !== false)
        .sort((a, b) => Number(a?.sort_order ?? 0) - Number(b?.sort_order ?? 0));

      const centsToDollars = (amount: any) => Number(amount || 0) / 100;

      // Defensive fallback: if the API returns incomplete pricing matrices (e.g. missing
      // additional-system pricing or 2-visit pricing), fill from the brand pricing matrix.
      const fallbackMatrixByCode: Record<string, PlanPricingMatrix> = {};
      for (const fallbackPlan of brandPlans || []) {
        fallbackMatrixByCode[fallbackPlan.code] = {
          base_price_monthly_1_pm: fallbackPlan.pricing.monthly_1_visit,
          base_price_monthly_2_pm: fallbackPlan.pricing.monthly_2_visits,
          base_price_annual_1_pm: fallbackPlan.pricing.annual_1_visit,
          base_price_annual_2_pm: fallbackPlan.pricing.annual_2_visits,
          additional_system_monthly_1_pm: fallbackPlan.pricing.additional_monthly_1_visit,
          additional_system_monthly_2_pm: fallbackPlan.pricing.additional_monthly_2_visits,
          additional_system_annual_1_pm: fallbackPlan.pricing.additional_annual_1_visit,
          additional_system_annual_2_pm: fallbackPlan.pricing.additional_annual_2_visits,
        };
      }

      const fillMissingPricing = (pricing: PlanPricingMatrix, fallback?: PlanPricingMatrix): PlanPricingMatrix => {
        if (!fallback) return pricing;

        const merged: PlanPricingMatrix = { ...pricing };
        const keys: Array<keyof PlanPricingMatrix> = [
          'base_price_monthly_1_pm',
          'base_price_monthly_2_pm',
          'base_price_annual_1_pm',
          'base_price_annual_2_pm',
          'additional_system_monthly_1_pm',
          'additional_system_monthly_2_pm',
          'additional_system_annual_1_pm',
          'additional_system_annual_2_pm',
        ];

        for (const key of keys) {
          const v = Number(merged[key]);
          if (!Number.isFinite(v) || v <= 0) {
            merged[key] = fallback[key];
          }
        }

        // Handle a common failure mode: the API has a value but it's identical to the 1-visit
        // price due to a missing update, while the fallback matrix differs.
        if (
          merged.base_price_monthly_2_pm === merged.base_price_monthly_1_pm &&
          fallback.base_price_monthly_2_pm !== fallback.base_price_monthly_1_pm
        ) {
          merged.base_price_monthly_2_pm = fallback.base_price_monthly_2_pm;
        }

        if (
          merged.base_price_annual_2_pm === merged.base_price_annual_1_pm &&
          fallback.base_price_annual_2_pm !== fallback.base_price_annual_1_pm
        ) {
          merged.base_price_annual_2_pm = fallback.base_price_annual_2_pm;
        }

        return merged;
      };

      const mapped: EnrollmentPlanCard[] = activePlans.map((plan, idx): EnrollmentPlanCard => {
        const features = normalizeFeatures(plan.features);
        const coverageDetails = normalizeFeatures(plan.coverage_details);
        const code = plan.plan_code;

        const rawPricing: PlanPricingMatrix = {
          base_price_monthly_1_pm: centsToDollars(plan.base_price_monthly_1_pm),
          base_price_monthly_2_pm: centsToDollars(plan.base_price_monthly_2_pm),
          base_price_annual_1_pm: centsToDollars(plan.base_price_annual_1_pm),
          base_price_annual_2_pm: centsToDollars(plan.base_price_annual_2_pm),
          additional_system_monthly_1_pm: centsToDollars(plan.additional_system_monthly_1_pm),
          additional_system_monthly_2_pm: centsToDollars(plan.additional_system_monthly_2_pm),
          additional_system_annual_1_pm: centsToDollars(plan.additional_system_annual_1_pm),
          additional_system_annual_2_pm: centsToDollars(plan.additional_system_annual_2_pm),
        };

        const pricing = fillMissingPricing(rawPricing, fallbackMatrixByCode[code]);

        return {
          id: `${code}-${brandInfo.companyId}`,
          code,
          name: plan.plan_name || code,
          description: plan.description || undefined,
          features: features.length ? features : coverageDetails,
          color: palette[idx] || palette[0] || '#0066CC',
          popular: code === 'premier',
          basePrice: pricing.base_price_monthly_1_pm,
          pricing,
        };
      });

      return mapped;
    }

    const mapped: EnrollmentPlanCard[] = (brandPlans || []).map((plan, idx) => {
      const pricing: PlanPricingMatrix = {
        base_price_monthly_1_pm: plan.pricing.monthly_1_visit,
        base_price_monthly_2_pm: plan.pricing.monthly_2_visits,
        base_price_annual_1_pm: plan.pricing.annual_1_visit,
        base_price_annual_2_pm: plan.pricing.annual_2_visits,
        additional_system_monthly_1_pm: plan.pricing.additional_monthly_1_visit,
        additional_system_monthly_2_pm: plan.pricing.additional_monthly_2_visits,
        additional_system_annual_1_pm: plan.pricing.additional_annual_1_visit,
        additional_system_annual_2_pm: plan.pricing.additional_annual_2_visits,
      };

      return {
        id: plan.id,
        code: plan.code,
        name: plan.name,
        description: plan.description,
        features: (plan.features || [])
          .map((feature: any) => (typeof feature === 'string' ? feature : feature?.text))
          .filter((feature: any): feature is string => Boolean(feature)),
        color: palette[idx] || plan.color,
        popular: plan.popular,
        basePrice: pricing.base_price_monthly_1_pm,
        pricing,
      };
    });

    return mapped;
  }, [
    membershipPlans,
    brandKey,
    brandPlans,
    brandInfo.companyId,
    brandInfo.colors.primary,
    brandInfo.colors.secondary,
    brandInfo.colors.accent,
  ]);

  useEffect(() => {
    if (!initialPlanId) return;
    const plan = enrollmentPlans.find((item) => item.id === initialPlanId || item.id.endsWith(initialPlanId))
      || enrollmentPlans.find((p) => p.code === initialPlanId.replace(/-.*$/, ''));
    if (!plan) return;

    const price = plan.pricing ? plan.pricing.base_price_monthly_1_pm : plan.basePrice;

    setMembershipSelection({
      planId: plan.id,
      planCode: plan.code,
      planName: plan.name,
      visits: 1,
      systems: 1,
      billingFrequency: 'monthly',
      price,
    });
  }, [initialPlanId, enrollmentPlans]);

  useEffect(() => {
    autoSubmittedInitialPhoneRef.current = false;
  }, [initialPhone]);

  const submitPhone = async (rawPhone: string) => {
    setLoading(true);
    setError(null);

    try {
      let cleanedPhone = String(rawPhone || '').replace(/\D/g, '');

      // Normalize common US formats (allow leading country code 1).
      if (cleanedPhone.length === 11 && cleanedPhone.startsWith('1')) {
        cleanedPhone = cleanedPhone.slice(1);
      }

      // Guard: the downstream SuccessWare webhook requires a valid phoneNumber.
      if (cleanedPhone.length < 10) {
        setError('Please enter a valid 10-digit phone number.');
        return;
      }

      // Search for customer in SuccessWare
      const response = await fetch(`${API_BASE_URL}/api/memberships/search-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company: brandInfo.key,
          phone: cleanedPhone,
        }),
      });

      let data: any = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        if (response.status === 400 || response.status === 404) {
          // Treat 400/404 as "no customer found" (some brands return 400 for no-match).
          data = { found: false, customers: [], customer: null, customersCount: 0 };
        } else {
          const message =
            (data && (data.message || data.error)) || `Search failed (status ${response.status})`;
          throw new Error(message);
        }
      }

      // Normalize legacy array responses (raw SuccessWare search results) into the expected shape.
      let normalized = data;
      if (Array.isArray(data)) {
        const normalizeLegacyCustomer = (record: any): CustomerData => {
          const location = record?.location || {};
          const addressLine =
            location.fullAddress ||
            [location.address1 || location.address, location.address2, location.city, location.state, location.zipCode]
              .filter(Boolean)
              .join(', ')
              .trim();

          return {
            phone: String(record?.customer?.phoneNumber || '').replace(/\D/g, '') || cleanedPhone,
            isMobile: true,
            firstName: record?.customer?.firstName || '',
            lastName: record?.customer?.lastName || '',
            email: record?.customer?.email || '',
            address: addressLine,
            city: location.city || '',
            state: location.state || '',
            zipCode: location.zipCode || '',
            serviceAccountId: record?.serviceAccountId || record?.customer?.serviceAccountId,
            customerId: record?.customerId || record?.customer?.customerId,
            locationId: record?.locationId || location.locationId,
            existingCustomer: true,
          };
        };

        const customers = data.map(normalizeLegacyCustomer).filter(Boolean);
        normalized = {
          found: customers.length > 0,
          customers,
          customer: customers[0] || null,
          customersCount: customers.length,
        };
      }

      if (normalized?.found && Array.isArray(normalized.customers) && normalized.customers.length > 1) {
        // Multiple matches for this phone; ask the user which record is correct.
        setCustomerMatches(normalized.customers);
        setCustomerData((prev) => ({
          ...prev,
          phone: cleanedPhone,
          existingCustomer: true,
        }));
        setStep('customer-select');
      } else if (normalized?.found && normalized.customer) {
        // Customer exists - populate their data
        // Only mark as existing customer if we have the required SuccessWare IDs for updating
        const hasRequiredIds = Boolean(
          normalized.customer.serviceAccountId && normalized.customer.customerId
        );
        setCustomerMatches(null);
        setCustomerData((prev) => {
          return {
            ...prev,
            ...normalized.customer,
            // Preserve the entered phone if the API doesn't provide one
            phone: normalized.customer.phone || cleanedPhone,
            existingCustomer: hasRequiredIds,
          };
        });
        setStep('customer-info'); // Go to confirmation step
      } else {
        // New customer - move to info collection (preserve the phone number!)
        setCustomerMatches(null);
        setCustomerData((prev) => ({
          ...prev,
          phone: cleanedPhone,
          existingCustomer: false,
        }));
        setStep('customer-info');
      }
    } catch (err) {
      setError('Failed to search for customer. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialPhone) return;
    if (step !== 'phone') return;

    const cleaned = String(initialPhone || '').replace(/\D/g, '');
    if (cleaned) {
      setCustomerData((prev) => ({
        ...prev,
        phone: cleaned,
      }));
    }

    if (!autoSubmitInitialPhone) return;
    if (autoSubmittedInitialPhoneRef.current) return;
    if (cleaned.length < 10) return;

    autoSubmittedInitialPhoneRef.current = true;
    trackEvent('membership_wizard_phone_submit', { source: 'hero_auto' });
    submitPhone(cleaned);
  }, [initialPhone, autoSubmitInitialPhone, step]);

  useEffect(() => {
    if (step !== 'plan-selection') return;
    trackEvent('membership_plan_view', {
      recommended_plan_id: initialPlanId,
    });
  }, [step, initialPlanId]);

  // Step 1: Phone Number Collection
  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackEvent('membership_wizard_phone_submit', { source: 'manual' });
    submitPhone(customerData.phone);
  };

  // Step 2: Customer Info Confirmation/Collection
  const handleCustomerInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const fullServiceAddress = [customerData.address, customerData.address2]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ')
        .trim();

      let successwareIds: {
        serviceAccountId?: string;
        customerId?: string;
        locationId?: string;
      } = {
        serviceAccountId: customerData.serviceAccountId,
        customerId: customerData.customerId,
        locationId: customerData.locationId,
      };

      if (customerData.existingCustomer) {
        // Update existing customer
        const response = await fetch(`${API_BASE_URL}/api/memberships/update-customer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company: brandInfo.key,
            serviceAccountId: customerData.serviceAccountId,
            customerId: customerData.customerId,
            customer: {
              firstName: customerData.firstName,
              lastName: customerData.lastName,
              phoneNumber: customerData.phone.replace(/\D/g, ''),
              email: customerData.email,
            },
          }),
        });

        let updateData: any = null;
        try {
          updateData = await response.json();
        } catch {
          // ignore parse errors
        }

        if (!response.ok) {
          throw new Error(updateData?.message || updateData?.error || 'Failed to update customer');
        }
      } else {
        // Create new customer
        const response = await fetch(`${API_BASE_URL}/api/memberships/create-customer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company: brandInfo.key,
            customer: {
              firstName: customerData.firstName,
              lastName: customerData.lastName,
              phoneNumber: customerData.phone.replace(/\D/g, ''),
              email: customerData.email,
            },
            location: {
              address1: fullServiceAddress,
              city: customerData.city,
              state: customerData.state,
              zipCode: customerData.zipCode,
            },
          }),
        });

        let data: any = null;
        try {
          data = await response.json();
        } catch {
          // ignore parse errors
        }

        if (!response.ok) {
          throw new Error(data?.message || data?.error || 'Failed to create customer');
        }

        successwareIds = {
          serviceAccountId: data.serviceAccountId,
          customerId: data.customerId,
          locationId: data.locationId,
        };

        setCustomerData({
          ...customerData,
          serviceAccountId: successwareIds.serviceAccountId,
          customerId: successwareIds.customerId,
          locationId: successwareIds.locationId,
        });
      }

      // Internal lead notify (best-effort; do not block the user flow).
      if (!leadNotifyAttemptedRef.current) {
        leadNotifyAttemptedRef.current = true;
        try {
          const leadRes = await fetch(`${API_BASE_URL}/api/memberships/lead-notify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              company_id: companyId,
              lead: {
                phone: customerData.phone.replace(/\D/g, ''),
                first_name: customerData.firstName,
                last_name: customerData.lastName,
                email: customerData.email,
                address: fullServiceAddress,
                address2: customerData.address2 || '',
                city: customerData.city,
                state: customerData.state,
                zip: customerData.zipCode,
                existing_customer: customerData.existingCustomer,
                successware: {
                  service_account_id: successwareIds.serviceAccountId,
                  customer_id: successwareIds.customerId,
                  location_id: successwareIds.locationId,
                },
                preferences: {
                  plan_id: initialPlanId || null,
                  systems: typeof initialSystems === 'number' ? initialSystems : null,
                  billing_frequency:
                    initialBillingFrequency === 'annual' || initialBillingFrequency === 'monthly'
                      ? initialBillingFrequency
                      : null,
                },
                source: 'wizard_customer_info',
                domain: window.location.hostname,
              },
            }),
          });

          const leadJson = await leadRes.json().catch(() => null);
          if (!leadRes.ok) {
            console.warn('lead-notify failed', leadRes.status, leadJson);
          }
        } catch (leadErr) {
          console.warn('lead-notify error', leadErr);
        }
      }

      // Move to plan selection (where prices are revealed!)
      setStep('plan-selection');
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : 'Failed to save customer information. Please try again.';
      setError(message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Plan Selection
  const handlePlanSelection = (plan: MembershipSelection) => {
    setMembershipSelection(plan);

    trackEvent('membership_plan_selected', {
      plan_id: plan.planId,
      plan_code: plan.planCode,
      billing_frequency: plan.billingFrequency,
      systems: plan.systems,
      visits: plan.visits,
      value: plan.price,
      currency: 'USD',
    });

    trackBeginCheckout({
      value: plan.price,
      currency: 'USD',
      plan_id: plan.planId,
      plan_code: plan.planCode,
      billing_frequency: plan.billingFrequency,
      systems: plan.systems,
      visits: plan.visits,
    });
    setStep('payment');
  };

  // Step 4: Payment Processing
  const handlePaymentSubmit = async (
    paymentMethodId: string,
    termsAccepted: {
      timestamp: string;
      terms_version?: string;
      brand_key?: string;
      signature_name?: string;
      user_agent?: string;
    },
    paymentMethodType: 'card' | 'bank',
    discountCode?: string | null,
    staxCustomerId?: string | null
  ) => {
    setLoading(true);
    setError(null);

    if (!membershipSelection) {
      setLoading(false);
      return;
    }

    const basePriceCents = Math.round(membershipSelection.price * 100);

    const shouldApplyDiscount =
      Boolean(discountCode) &&
      Boolean(appliedDiscount) &&
      String(appliedDiscount.code || '').toUpperCase() === String(discountCode || '').toUpperCase();

    const discountAmountCents = shouldApplyDiscount
      ? Math.round(
          calculateDiscount(
            membershipSelection.price,
            appliedDiscount.discount_type,
            Number(appliedDiscount.discount_value)
          ) * 100
        )
      : 0;

    const finalPriceCents = Math.max(
      0,
      basePriceCents - Math.min(basePriceCents, Math.max(0, discountAmountCents))
    );

    try {
      // Create membership and process payment
      const response = await fetch(`${API_BASE_URL}/api/memberships/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enrollment_source: 'membership_signup_site',
          company_id: companyId,
          sold_by_technician_name: technicianAttribution.name.trim() || undefined,
          sold_by_technician_email: technicianAttribution.email.trim() || undefined,
          plan_id: membershipSelection.planId,
          plan_code: membershipSelection.planCode,
          plan_name: membershipSelection.planName,
          billing_frequency: membershipSelection.billingFrequency,
          tune_ups_per_year: membershipSelection.visits,
          systems_count: membershipSelection.systems,
          base_price_cents: basePriceCents,
          discount_code: shouldApplyDiscount ? appliedDiscount.code : undefined,
          discount_amount_cents: discountAmountCents,
          final_price_cents: finalPriceCents,
          // Production enroll API requires `payment_token` (legacy naming). The Stax
          // tokenization id works as the token value here.
          payment_token: paymentMethodId,
          payment_method_id: paymentMethodId,
          stax_customer_id: staxCustomerId || undefined,
          customer_id: staxCustomerId || undefined,
          payment_method_type: paymentMethodType,
          terms_accepted: termsAccepted,
          customer: {
            email: customerData.email,
            first_name: customerData.firstName,
            last_name: customerData.lastName,
            phone: customerData.phone.replace(/\D/g, ''),
            address: [customerData.address, customerData.address2]
              .map((part) => String(part || '').trim())
              .filter(Boolean)
              .join(' ')
              .trim(),
            city: customerData.city,
            state: customerData.state,
            zip: customerData.zipCode,
            is_mobile: customerData.isMobile,
          },
          successware: {
            customer_id: customerData.customerId,
            service_account_id: customerData.serviceAccountId,
            location_id: customerData.locationId,
          }
        }),
      });

      let data: any = null;
      try {
        data = await response.json();
      } catch {
        // ignore parse errors; we'll fall back to generic message
      }

      if (!response.ok) {
        const message = (data && (data.message || data.error)) || 'Payment failed';
        throw new Error(message);
      }

      const membershipId = data?.membership_id ? String(data.membership_id) : '';
      if (membershipId) {
        setEnrollmentResult({
          membershipId,
          nextBillingDate: data?.next_billing_date ? String(data.next_billing_date) : undefined,
        });
      }

      trackPurchase({
        value: finalPriceCents / 100,
        currency: 'USD',
        transaction_id: data?.transaction_id ? String(data.transaction_id) : undefined,
        membership_id: membershipId || undefined,
        plan_id: membershipSelection.planId,
        plan_code: membershipSelection.planCode,
        billing_frequency: membershipSelection.billingFrequency,
        systems: membershipSelection.systems,
        visits: membershipSelection.visits,
      });

      setScheduledVisit(null);

      // Move to scheduling
      setStep('scheduling');
    } catch (err: any) {
      const message = err?.message || 'Payment failed. Please try again.';
      setError(message);
      console.error('Enrollment error:', err);
      // Re-throw so PaymentStep can also show this message if needed
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Step 5: Preventive Maintenance Scheduling
  const handleSchedulingSubmit = async (selection: ScheduleSelection) => {
    setScheduleSelection(selection);
    setLoading(true);
    setError(null);

    const membershipId = enrollmentResult?.membershipId;
    let visitId: string | undefined;

    try {
      if (!membershipId) {
        throw new Error('Missing enrollment confirmation (membership id). Please refresh and try again.');
      }

      if (selection.wantsScheduling) {
        if (!selection.date || !selection.timeSlot) {
          throw new Error('Please select a date and time window.');
        }

        // SuccessWare webhook expects priorityLevel 1 (normal) or 2 (priority).
        const priorityLevel = selection.equipmentAge === '8_or_more' ? 2 : 1;

          const response = await fetch(`${API_BASE_URL}/api/memberships/schedule-maintenance`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              membership_id: membershipId,
            visit_date: selection.date,
            visit_window: selection.timeSlot,
            visit_type: 'pm',
            equipment_age: selection.equipmentAge,
            priority_level: priorityLevel,
          }),
        });

        const scheduleData = await response.json().catch(() => null);

        if (!response.ok) {
          const message = scheduleData?.message || scheduleData?.error || 'Failed to schedule service';
          throw new Error(message);
        }

        setScheduledVisit(scheduleData?.visit || null);
        visitId = scheduleData?.visit?.id;
      }

      setStep('complete');

      // Send confirmation email (best-effort). Scheduling should still be considered successful even if email fails.
      try {
        const emailRes = await fetch(`${API_BASE_URL}/api/memberships/send-confirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            membership_id: membershipId,
            visit_id: visitId,
          }),
        });

        const emailJson = await emailRes.json().catch(() => ({}));
        if (emailRes.ok && emailJson?.sent) {
          setConfirmationEmail({ attempted: true, sent: true });
        } else {
          setConfirmationEmail({
            attempted: true,
            sent: false,
            message:
              emailJson?.message ||
              emailJson?.error ||
              `Email not sent (status ${emailRes.status}).`,
          });
        }
      } catch (err: any) {
        console.error('Failed to send confirmation email:', err);
        setConfirmationEmail({
          attempted: true,
          sent: false,
          message: err?.message || 'Failed to send confirmation email.',
        });
      }

      if (onComplete) onComplete();
    } catch (err: any) {
      const rawMessage =
        err?.message ||
        'Failed to schedule service. Please try another date and time window.';

      const message = String(rawMessage || '');
      setError(message);
      console.error(err);
      setStep('scheduling');
    } finally {
      setLoading(false);
    }
  };

  // Render current step
  const renderStep = () => {
    switch (step) {
      case 'phone':
        return <PhoneStep 
          phone={customerData.phone}
          onChange={(phone) => setCustomerData({ ...customerData, phone })}
          onSubmit={handlePhoneSubmit}
          loading={loading}
        />;

      case 'customer-select':
        return <CustomerSelectStep
          customers={customerMatches || []}
          onBack={() => { setCustomerMatches(null); setStep('phone'); }}
          onSelect={(selected) => {
            setCustomerMatches(null);
            // Only mark as existing customer if we have the required SuccessWare IDs
            const hasRequiredIds = Boolean(
              selected.serviceAccountId && selected.customerId
            );
            setCustomerData((prev) => {
              const cleanedPhone = prev.phone.replace(/\D/g, '');
              return {
                ...prev,
                ...selected,
                phone: selected.phone || cleanedPhone,
                existingCustomer: hasRequiredIds,
              };
            });
            setStep('customer-info');
          }}
        />;

      case 'customer-info':
        return <CustomerInfoStep
          customer={customerData}
          onChange={setCustomerData}
          onSubmit={handleCustomerInfoSubmit}
          loading={loading}
        />;

      case 'plan-selection':
        return <PlanSelectionStep
          onSelect={handlePlanSelection}
          loading={loading}
          recommendedPlanId={initialPlanId}
          initialSystems={initialSystems}
          initialBillingFrequency={initialBillingFrequency}
          plans={enrollmentPlans}
        />;

      case 'payment':
        return <PaymentStep
          membership={membershipSelection!}
          customer={customerData}
          technicianAttribution={technicianAttribution}
          setTechnicianAttribution={setTechnicianAttribution}
          onSubmit={handlePaymentSubmit}
          onBack={() => setStep('plan-selection')}
          loading={loading}
          discountCodes={discountCodes}
          discountCodesLoading={discountCodesLoading}
          promoCode={promoCode}
          setPromoCode={setPromoCode}
          appliedDiscount={appliedDiscount}
          setAppliedDiscount={setAppliedDiscount}
        />;

      case 'scheduling':
        return <SchedulingStep
          schedule={scheduleSelection}
          onChange={setScheduleSelection}
          onSubmit={handleSchedulingSubmit}
          onSkip={() => handleSchedulingSubmit({ wantsScheduling: false })}
          loading={loading}
        />;

      case 'complete':
        return (
        <CompleteStep
          customer={customerData}
          membership={membershipSelection!}
          enrollment={enrollmentResult}
          schedule={scheduleSelection}
          scheduledVisit={scheduledVisit}
          confirmationEmail={confirmationEmail}
          errorMessage={error}
        />
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={`${
        step === 'plan-selection' || step === 'payment' || step === 'scheduling' || step === 'complete'
          ? 'max-w-6xl'
          : 'max-w-4xl'
      } mx-auto`}
    >
      {/* Progress Indicator */}
      <div ref={wizardScrollRef} className="mb-8">
        <StepIndicator currentStep={step} />
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2 text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Current Step Content */}
      <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
        {renderStep()}
      </div>
    </div>
  );
}

// Step Indicator Component
function StepIndicator({ currentStep }: { currentStep: Step }) {
  // Keep the flow as-is, but reduce the perceived complexity of the stepper.
  // We intentionally collapse multiple internal steps into a smaller set of “customer-visible” steps.
  const steps = [
    { id: 'phone', label: 'Verify Info' },
    { id: 'plan-selection', label: 'Choose Plan' },
    { id: 'payment', label: 'Payment' },
    { id: 'complete', label: 'Done' },
  ];

  const normalizedStep =
    currentStep === 'customer-select' || currentStep === 'customer-info'
      ? 'phone'
      : currentStep === 'scheduling'
        ? 'payment'
        : currentStep;
  const currentIndex = steps.findIndex(s => s.id === normalizedStep);

  return (
    <div className="flex items-center justify-between">
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                index <= currentIndex
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {index < currentIndex ? <Check className="h-5 w-5" /> : index + 1}
            </div>
            <span className="text-xs mt-2 font-medium text-gray-600">{step.label}</span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`flex-1 h-1 mx-2 ${
                index < currentIndex ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// Phone Step Component
function PhoneStep({ phone, onChange, onSubmit, loading }: any) {
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
          <Phone className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-3xl font-bold mb-2">Let’s find your home</h2>
        <p className="text-gray-600 max-w-xl mx-auto">
          Enter your phone number and we’ll pull up your address + service history so you can see your personalized membership pricing.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number *
        </label>
        <input
          type="tel"
          autoComplete="tel"
          required
          value={phone}
          onChange={(e) => onChange(e.target.value)}
          placeholder="(561) 555-1234"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
        />
        <p className="text-xs text-gray-500 mt-2">
          🔒 Your phone is only used to look up your home’s details. We never share or sell your info.
        </p>
      </div>

      <button
        type="submit"
        disabled={loading || !phone}
        className="w-full py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-lg"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Searching...</span>
          </>
        ) : (
          <span>Find My Home →</span>
        )}
      </button>

      <p className="text-xs text-gray-500 text-center">
        No card required to see pricing. Review everything first—pay only when you’re ready.
      </p>
    </form>
  );
}

// Customer Select Step Component
function CustomerSelectStep({
  customers,
  onSelect,
  onBack,
}: {
  customers: CustomerData[];
  onSelect: (customer: CustomerData) => void;
  onBack: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
          <User className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Select Your Account</h2>
        <p className="text-gray-600 mt-2">
          We found multiple customer records for this phone number. Please choose the correct one.
        </p>
      </div>

      <div className="space-y-3">
        {customers.map((c, idx) => {
          const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim() || 'Customer';
          const addressLine = [c.address, c.city, c.state, c.zipCode]
            .filter(Boolean)
            .join(', ')
            .trim();

          return (
            <button
              key={`${c.customerId || 'unknown'}-${idx}`}
              type="button"
              onClick={() => onSelect(c)}
              className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold text-gray-900">{name}</div>
                  {c.email && <div className="text-sm text-gray-600">{c.email}</div>}
                  {addressLine && <div className="text-sm text-gray-600 mt-1">{addressLine}</div>}
                </div>
                <div className="text-sm font-medium text-blue-700">Select</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          ← Back
        </button>
      </div>
    </div>
  );
}

// Customer Info Step Component  
function CustomerInfoStep({ customer, onChange, onSubmit, loading }: any) {
  const isExisting = customer.existingCustomer;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
          <User className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-3xl font-bold mb-2">
          {isExisting ? 'Confirm Your Information' : 'Tell Us About Yourself'}
        </h2>
        <p className="text-gray-600 max-w-xl mx-auto">
          {isExisting
            ? 'Confirm your contact information so we can apply Comfort Club benefits to the right home.'
            : 'A few details so we can create your membership and apply benefits to the right address.'}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name *
          </label>
          <input
            type="text"
            required
            autoComplete="given-name"
            placeholder="First name"
            value={customer.firstName}
            onChange={(e) => onChange({ ...customer, firstName: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            type="text"
            required
            autoComplete="family-name"
            placeholder="Last name"
            value={customer.lastName}
            onChange={(e) => onChange({ ...customer, lastName: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            value={customer.email}
            onChange={(e) => onChange({ ...customer, email: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number *
          </label>
          <div className="space-y-2">
            <input
              type="tel"
              required
              autoComplete="tel"
              placeholder="(561) 555-1234"
              value={customer.phone}
              onChange={(e) => onChange({ ...customer, phone: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={customer.isMobile}
                onChange={(e) => onChange({ ...customer, isMobile: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">This number can receive text updates</span>
            </label>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Service Address *
        </label>
        <AddressAutocomplete
          value={customer.address}
          onChange={(val) => onChange({ ...customer, address: val })}
          onSelect={(addr) => onChange({
            ...customer,
            address: addr.street,
            city: addr.city,
            state: addr.state,
            zipCode: addr.zipCode,
          })}
          placeholder="123 Main Street"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Address Line 2 (Unit/Apt/Suite)
        </label>
        <input
          type="text"
          autoComplete="address-line2"
          placeholder="Apt 4B"
          value={customer.address2 || ''}
          onChange={(e) => onChange({ ...customer, address2: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            City *
          </label>
          <input
            type="text"
            required
            autoComplete="address-level2"
            value={customer.city}
            onChange={(e) => onChange({ ...customer, city: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            State *
          </label>
          <select
            required
            value={customer.state}
            onChange={(e) => onChange({ ...customer, state: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {US_STATES.map((state) => (
              <option key={state.code} value={state.code}>
                {state.code} — {state.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ZIP Code *
          </label>
          <input
            type="text"
            required
            autoComplete="postal-code"
            value={customer.zipCode}
            onChange={(e) => onChange({ ...customer, zipCode: e.target.value })}
            placeholder="33601"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-lg"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Saving...</span>
          </>
        ) : (
          <span>Continue to Plans</span>
        )}
      </button>
    </form>
  );
}

// Plan Selection Step - Complete Implementation
function PlanSelectionStep({
  onSelect,
  loading,
  recommendedPlanId,
  initialSystems = 1,
  initialBillingFrequency = 'monthly',
  plans: providedPlans,
}: {
  onSelect: (plan: MembershipSelection) => void;
  loading: boolean;
  recommendedPlanId?: string;
  initialSystems?: number;
  initialBillingFrequency?: 'monthly' | 'annual';
  plans: EnrollmentPlanCard[];
}) {
  const [visits, setVisits] = useState<1 | 2>(1);
  const brandKey = detectBrandFromHost();
  const brandInfo = React.useMemo(() => getBrandInfo(brandKey), [brandKey]);
  const comparisonPlans = React.useMemo(() => getPlansForBrand(brandKey), [brandKey]);
  const comparisonData = React.useMemo(() => getPlanComparisonDataForBrand(brandKey), [brandKey]);
  const [systems, setSystems] = useState(() => {
    const v = Number(initialSystems);
    if (!Number.isFinite(v) || v <= 0) return 1;
    return Math.min(10, Math.max(1, Math.round(v)));
  });
  const [billingFrequency, setBillingFrequency] = useState<'monthly' | 'annual'>(
    initialBillingFrequency === 'annual' ? 'annual' : 'monthly'
  );

  useEffect(() => {
    const v = Number(initialSystems);
    if (!Number.isFinite(v) || v <= 0) return;
    setSystems(Math.min(10, Math.max(1, Math.round(v))));
  }, [initialSystems]);

  useEffect(() => {
    if (initialBillingFrequency !== 'annual' && initialBillingFrequency !== 'monthly') return;
    setBillingFrequency(initialBillingFrequency);
  }, [initialBillingFrequency]);

  const plans = React.useMemo(() => providedPlans || [], [providedPlans]);

  type ComparisonPlan = (typeof comparisonPlans)[number];

  const comparisonTablePlans: ComparisonPlan[] = comparisonPlans;

  const resolvePlanOption = (comparisonPlan: ComparisonPlan): EnrollmentPlanCard | null => {
    return (
      plans.find((p) => p.code === comparisonPlan.code && !p.annualOnly)
      || plans.find((p) => p.code === comparisonPlan.code)
      || null
    );
  };

  const calculatePrice = (plan: EnrollmentPlanCard, frequency?: 'monthly' | 'annual') => {
    const effectiveFrequency = frequency || billingFrequency;

    if (plan.pricing) {
      const basePrice =
        effectiveFrequency === 'monthly'
          ? (visits === 1
            ? plan.pricing.base_price_monthly_1_pm
            : plan.pricing.base_price_monthly_2_pm)
          : (visits === 1
            ? plan.pricing.base_price_annual_1_pm
            : plan.pricing.base_price_annual_2_pm);

      const additionalSystemPrice =
        effectiveFrequency === 'monthly'
          ? (visits === 1
            ? plan.pricing.additional_system_monthly_1_pm
            : plan.pricing.additional_system_monthly_2_pm)
          : (visits === 1
            ? plan.pricing.additional_system_annual_1_pm
            : plan.pricing.additional_system_annual_2_pm);

      const total = basePrice + additionalSystemPrice * Math.max(0, systems - 1);
      return total.toFixed(2);
    }

    // Fallback pricing for legacy hardcoded plans.
    let price = plan.basePrice;
    if (visits === 2) {
      price += 12.99 * systems;
    }
    if (systems > 1) {
      price += 16.99 * (systems - 1);
    }
    if (effectiveFrequency === 'annual') {
      price *= 12;
    }

    return price.toFixed(2);
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
          <Check className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-3xl font-bold mb-2">Choose Your Comfort Club Plan</h2>
        <p className="text-gray-600 max-w-xl mx-auto">
          Pick a plan and customize visits/systems. You’ll see your final price before you pay.
        </p>
      </div>

      {/* Configuration Options */}
      <div className="bg-gray-50 p-6 rounded-lg space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tune-Up Visits per Year
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setVisits(1)}
              className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                visits === 1
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              1 Visit / Year
            </button>
            <button
              type="button"
              onClick={() => setVisits(2)}
              className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                visits === 2
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              2 Visits / Year
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Number of Systems: {systems}
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSystems((s: number) => Math.max(1, s - 1))}
              disabled={systems <= 1}
              aria-label="Decrease systems"
              className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              −
            </button>

            <input
              type="range"
              min="1"
              max="10"
              value={systems}
              onChange={(e) => setSystems(parseInt(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />

            <button
              type="button"
              onClick={() => setSystems((s: number) => Math.min(10, s + 1))}
              disabled={systems >= 10}
              aria-label="Increase systems"
              className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              +
            </button>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>1</span>
            <span>5</span>
            <span>10</span>
          </div>
          {systems > 1 && (
            <p className="text-sm text-gray-600 mt-2">
              Additional systems are billed at the plan’s per-system rate.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Billing Frequency
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setBillingFrequency('monthly')}
              className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                billingFrequency === 'monthly'
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingFrequency('annual')}
              className={`flex-1 py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                billingFrequency === 'annual'
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              Annual <span className="text-sm">(12 months)</span>
            </button>
          </div>
        </div>
      </div>

      {/* Offer summary — aligned with table columns below */}
      <div
        className="grid gap-6"
        style={{ gridTemplateColumns: `repeat(${comparisonTablePlans.length + 1}, 1fr)` }}
      >
        {/* Spacer for the "Features" label column */}
        <div className="hidden md:block" />
        {comparisonTablePlans.map((comparisonPlan) => {
          const planOption = resolvePlanOption(comparisonPlan);
          if (!planOption) return null;

          const effectiveBillingFrequency: 'monthly' | 'annual' =
            planOption.annualOnly ? 'annual' : billingFrequency;
          const price = calculatePrice(planOption, effectiveBillingFrequency);

          const periodLabel = effectiveBillingFrequency === 'monthly' ? 'mo' : 'yr';

          return (
            <div
              key={comparisonPlan.id}
              className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
            >
              <div className="p-6">
                <div className="flex items-center justify-center gap-2">
                  {comparisonPlan.popular && (
                    <span className="inline-flex items-center rounded-full bg-blue-600 px-3 py-1 text-xs font-extrabold tracking-wide text-white shadow">
                      Most Popular
                    </span>
                  )}
                </div>

                <div className="mt-3 text-center">
                  <div className="text-2xl font-extrabold" style={{ color: comparisonPlan.color }}>
                    {comparisonPlan.name}
                  </div>
                  {comparisonPlan.tagline && (
                    <div className="mt-1 text-sm font-medium text-gray-600">{comparisonPlan.tagline}</div>
                  )}
                </div>

                <div className="mt-6 flex items-baseline justify-center gap-2">
                  <span className="text-4xl font-extrabold text-gray-900">${price}</span>
                  <span className="text-gray-600">/{periodLabel}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Full benefits table (single table) with CTA buttons at bottom */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <PlanComparisonTable
          plans={comparisonTablePlans}
          comparisonData={comparisonData}
          renderPlanFooterExtra={(comparisonPlan) => {
            const planOption = resolvePlanOption(comparisonPlan as ComparisonPlan);
            if (!planOption) return null;

            const effectiveBillingFrequency: 'monthly' | 'annual' =
              planOption.annualOnly ? 'annual' : billingFrequency;
            const price = calculatePrice(planOption, effectiveBillingFrequency);

            return (
              <button
                type="button"
                onClick={() =>
                  onSelect({
                    planId: planOption.id,
                    planCode: planOption.code,
                    planName: planOption.name,
                    visits,
                    systems,
                    billingFrequency: effectiveBillingFrequency,
                    price: parseFloat(price),
                  })
                }
                disabled={loading}
                className="w-full rounded-lg py-2 font-semibold text-sm transition-all disabled:opacity-50 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  backgroundColor: planOption.color,
                  color: 'white',
                }}
              >
                {`Choose ${comparisonPlan.name}`}
              </button>
            );
          }}
        />
      </div>

      <p className="text-xs text-gray-500 text-center">
        *Subject to terms and conditions. **Credits applied to replacement equipment purchases.
      </p>
    </div>
  );
}

// US States List - All 50 states
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

// Countries List
const COUNTRIES = [
  { code: 'USA', name: 'United States' }, { code: 'CAN', name: 'Canada' },
  { code: 'MEX', name: 'Mexico' }, { code: 'GBR', name: 'United Kingdom' },
  { code: 'AUS', name: 'Australia' }, { code: 'DEU', name: 'Germany' },
  { code: 'FRA', name: 'France' }, { code: 'ITA', name: 'Italy' },
  { code: 'ESP', name: 'Spain' }
];

// Payment Step - Card-only implementation with Stax.js
function PaymentStep({
  membership,
  customer,
  technicianAttribution,
  setTechnicianAttribution,
  onSubmit,
  onBack,
  loading,
  discountCodes = [],
  discountCodesLoading = false,
  promoCode,
  setPromoCode,
  appliedDiscount,
  setAppliedDiscount,
}: any) {
  const paymentMethod: 'card' | 'bank' = 'card';
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [promoError, setPromoError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [sameAsService, setSameAsService] = useState(true);
  const [showTechnicianAttribution, setShowTechnicianAttribution] = useState(false);
  const {
    isStaxReady,
    staxError,
    cardFormStatus,
    retryCardForm,
    tokenizePaymentMethodDetailed,
  } = useStax();
  const [billingAddress, setBillingAddress] = useState({
    address: customer.address || '',
    city: customer.city || '',
    state: customer.state || 'FL',
    zip: customer.zipCode || '',
    country: 'USA',
  });

  const bankStaxRef = useRef<any>(null); // Bank/ACH tokenization (no iframe fields)

  useEffect(() => {
    if (!appliedDiscount) return;

    const validation = validateDiscountCode(
      appliedDiscount,
      membership.planCode,
      membership.billingFrequency
    );

    if (!validation.valid) {
      setAppliedDiscount(null);
      setPromoError(validation.reason || 'This promo code is not valid for your selection.');
    }
  }, [appliedDiscount, membership.planCode, membership.billingFrequency, setAppliedDiscount]);

  const basePrice = Number(membership.price) || 0;
  const discountAmount = appliedDiscount
    ? calculateDiscount(
        basePrice,
        appliedDiscount.discount_type,
        Number(appliedDiscount.discount_value)
      )
    : 0;
  const clampedDiscountAmount = Math.min(basePrice, Math.max(0, discountAmount));
  const finalPrice = Math.max(0, basePrice - clampedDiscountAmount);


  const applyPromoCode = (source: 'manual' | 'url_param' = 'manual') => {
    const code = String(promoCode || '').trim().toUpperCase();
    setPromoCode(code);
    setPromoError('');

    if (!code) {
      setAppliedDiscount(null);
      return;
    }

    if (!Array.isArray(discountCodes) || discountCodes.length === 0) {
      setAppliedDiscount(null);
      setPromoError('Promo codes are not available right now. Please try again in a moment.');
      trackEvent('promo_code_apply', { code, success: false, reason: 'codes_unavailable', source });
      return;
    }

    const match = discountCodes.find(
      (d: any) => String(d?.code || '').trim().toUpperCase() === code
    );

    if (!match) {
      setAppliedDiscount(null);
      setPromoError('That promo code was not found.');
      trackEvent('promo_code_apply', { code, success: false, reason: 'not_found', source });
      return;
    }

    const validation = validateDiscountCode(match, membership.planCode, membership.billingFrequency);
    if (!validation.valid) {
      setAppliedDiscount(null);
      setPromoError(validation.reason || 'This promo code is not valid for your selection.');
      trackEvent('promo_code_apply', { code, success: false, reason: 'invalid_for_selection', source });
      return;
    }

    setAppliedDiscount(match);
    setPromoError('');
    trackEvent('promo_code_apply', {
      code,
      success: true,
      source,
      plan_code: membership.planCode,
      billing_frequency: membership.billingFrequency,
    });
  };

  // Auto-apply a URL-prefilled promo code once discount codes have loaded.
  const promoAutoApplyRef = useRef(false);
  useEffect(() => {
    if (promoAutoApplyRef.current) return;
    if (appliedDiscount) return;
    if (!String(promoCode || '').trim()) return;
    if (discountCodesLoading || !Array.isArray(discountCodes) || discountCodes.length === 0) return;
    promoAutoApplyRef.current = true;
    applyPromoCode('url_param');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountCodes, discountCodesLoading, promoCode, appliedDiscount]);

  const removePromoCode = () => {
    setAppliedDiscount(null);
    setPromoCode('');
    setPromoError('');
  };

  const validateAddress = () => {
    const rawAddress = sameAsService
      ? {
          address: customer.address,
          city: customer.city,
          state: customer.state,
          zip: customer.zipCode,
        }
      : billingAddress;

    if (!rawAddress.address || !rawAddress.city || !rawAddress.state || !rawAddress.zip) {
      setError('Please fill in all required address fields.');
      return null;
    }

    const rawState = (rawAddress.state || '').trim();

    // Normalize state to a strict 2-letter code for Stax validation
    let normalizedState = rawState;
    if (rawState.length === 2) {
      normalizedState = rawState.toUpperCase();
    } else {
      const match = US_STATES.find(
        (s) => s.name.toLowerCase() === rawState.toLowerCase() || s.code.toLowerCase() === rawState.toLowerCase()
      );
      if (match) {
        normalizedState = match.code;
      } else if (rawState.length > 2) {
        normalizedState = rawState.slice(0, 2).toUpperCase();
      }
    }

    if (normalizedState.length !== 2) {
      setError('State must be a 2-letter code (e.g., FL).');
      return null;
    }

    const addressToUse = { ...rawAddress, state: normalizedState };
    return addressToUse;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing(true);
    setError('');

    try {
      if (!isStaxReady) {
        throw new Error('Payment system is still loading. Please try again in a moment.');
      }

      if (paymentMethod === 'card' && cardFormStatus !== 'mounted') {
        throw new Error(
          staxError ||
            'Secure card fields are not ready yet. Please wait a moment, or refresh the page if the fields never appear.'
        );
      }

      const addressToUse = validateAddress();
      if (!addressToUse) {
        setProcessing(false);
        return;
      }

      if (!termsAccepted) {
        setError('Please accept the Terms & Conditions to continue.');
        setProcessing(false);
        return;
      }

      const normalizedSignatureName = String(signatureName || '').trim();
      if (!normalizedSignatureName) {
        setError('Please type your full name to sign the Terms & Conditions.');
        setProcessing(false);
        return;
      }

      const baseDetails = {
        firstname: customer.firstName,
        lastname: customer.lastName,
        phone: customer.phone,
        email: customer.email,
        address_1: addressToUse.address,
        address_city: addressToUse.city,
        address_state: addressToUse.state,
        address_zip: addressToUse.zip,
        address_country: sameAsService ? 'USA' : billingAddress.country,
      };

      let paymentToken: string;
      let staxCustomerId: string | undefined;

      if (paymentMethod === 'card') {
        const form = e.currentTarget as HTMLFormElement;

        const tokenResult = await tokenizePaymentMethodDetailed({
          form,
          extraDetails: baseDetails,
        });

        if (!tokenResult?.id) {
          throw new Error('Tokenization succeeded but no token ID was returned.');
        }

        paymentToken = tokenResult.id;
        staxCustomerId = tokenResult.customer_id || tokenResult.customer?.id || undefined;
      } else {
        throw new Error('Bank account payments are currently unavailable. Please use a credit/debit card.');
      }

      const effectiveBrandKey = detectBrandFromHost();

      await onSubmit(
        paymentToken,
        {
          timestamp: new Date().toISOString(),
          terms_version: getMembershipTermsVersion(effectiveBrandKey),
          brand_key: effectiveBrandKey,
          signature_name: normalizedSignatureName,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        },
        paymentMethod,
        appliedDiscount?.code || null,
        staxCustomerId
      );
    } catch (err: any) {
      console.error('Payment error:', err);
      const message = err?.message || err?.error || 'Payment failed. Please check your details and try again.';
      setError(message);
      setProcessing(false);
      return;
    }

    setProcessing(false);
  };

  const nextBillingDate = new Date();
  nextBillingDate.setDate(
    nextBillingDate.getDate() + (membership.billingFrequency === 'monthly' ? 30 : 365)
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
          <CreditCard className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-3xl font-bold mb-2">Payment Information</h2>
        <p className="text-gray-600 max-w-xl mx-auto">Securely complete your membership enrollment.</p>
      </div>

      <div className="text-center text-sm text-gray-600">
        Payments are accepted by credit/debit card.
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Order Summary - compact */}
        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          <h3 className="text-lg font-bold">Order Summary</h3>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Plan</span>
              <span className="font-semibold">{membership.planName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Systems</span>
              <span className="font-semibold">{membership.systems}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Visits / Year</span>
              <span className="font-semibold">{membership.visits}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Billing</span>
              <span className="font-semibold">
                {membership.billingFrequency === 'monthly'
                  ? 'Monthly'
                  : 'Annual'}
              </span>
            </div>
          </div>

          <div className="border-t pt-3 mt-3">
            {appliedDiscount && (
              <div className="flex justify-between text-sm">
                <span className="text-green-700 font-medium">
                  Promo ({String(appliedDiscount.code || '').toUpperCase()})
                </span>
                <span className="text-green-700 font-semibold">- ${clampedDiscountAmount.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between items-baseline">
              <span className="text-gray-600 text-sm">Due Today</span>
              <div className="text-right">
                {appliedDiscount && (
                  <div className="text-sm text-gray-500 line-through">${basePrice.toFixed(2)}</div>
                )}
                <div className="text-3xl font-bold text-blue-600">${finalPrice.toFixed(2)}</div>
                <div className="text-xs text-gray-500">
                  {membership.billingFrequency === 'monthly'
                    ? 'per month'
                    : 'per year'}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900">Promo code</div>
              {appliedDiscount && (
                <button
                  type="button"
                  onClick={removePromoCode}
                  className="text-xs font-semibold text-gray-600 hover:text-gray-900"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={promoCode}
                disabled={Boolean(appliedDiscount)}
                onChange={(e) => {
                  setPromoError('');
                  setPromoCode(e.target.value.toUpperCase());
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyPromoCode();
                  }
                }}
                placeholder="SAVE20"
                className="flex-1 h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
              />
              {!appliedDiscount && (
                <button
                  type="button"
                  onClick={() => applyPromoCode()}
                  disabled={discountCodesLoading || !String(promoCode || '').trim()}
                  className="h-10 px-4 bg-gray-900 text-white font-semibold rounded-md hover:bg-black disabled:opacity-50 text-sm"
                >
                  {discountCodesLoading ? 'Loading…' : 'Apply'}
                </button>
              )}
            </div>

            {promoError && <div className="mt-2 text-xs text-red-700">{promoError}</div>}
            {!promoError && appliedDiscount && (
              <div className="mt-2 text-xs text-green-700">
                Code applied: {appliedDiscount.code_name || appliedDiscount.code}
              </div>
            )}
          </div>

          <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded space-y-1">
            <div className="font-semibold">
              Next {membership.billingFrequency === 'monthly' ? 'Payment' : 'Renewal'} Date
            </div>
            <div>
              {nextBillingDate.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
            {membership.billingFrequency === 'monthly' ? (
              <div className="text-[11px] text-gray-600">
                Monthly billing is a 12-month commitment (12 payments). Canceling early does not waive remaining
                payments.
              </div>
            ) : (
              <div className="text-[11px] text-gray-600">
                Annual memberships renew yearly unless you cancel per the Terms & Conditions.
              </div>
            )}
          </div>
        </div>

        {/* Payment + Billing */}
        <div className="space-y-4">
          {/* Card fields */}
          <div className="space-y-3">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
              <h3 className="text-base font-semibold text-gray-900">Card Details</h3>

              {cardFormStatus !== 'mounted' && (
                <div
                  className={`rounded-lg border px-3 py-2 text-xs flex items-start gap-2 ${
                    cardFormStatus === 'loading'
                      ? 'bg-blue-50 border-blue-200 text-blue-800'
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`}
                >
                  {cardFormStatus === 'loading' ? (
                    <>
                      <Loader2 className="h-4 w-4 mt-0.5 animate-spin" />
                      <span>Loading secure card fields…</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-semibold">Card fields failed to load</div>
                        <div className="mt-0.5">{staxError || 'Please refresh the page and try again.'}</div>
                      </div>
                      <button
                        type="button"
                        onClick={retryCardForm}
                        className="shrink-0 font-semibold underline"
                      >
                        Retry
                      </button>
                    </>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Card Number *</label>
                <div
                  id="card-number"
                  className="w-full px-4 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent overflow-hidden"
                  style={{ height: '48px' }}
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month *</label>
                  <select
                    name="month"
                    required
                    className="w-full h-12 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white"
                    autoComplete="cc-exp-month"
                    defaultValue=""
                  >
                    <option value="" disabled>Month</option>
                    {Array.from({ length: 12 }, (_, i) => {
                      const m = (i + 1).toString().padStart(2, '0');
                      return <option key={m} value={m}>{m}</option>;
                    })}
                  </select>
                </div>

                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                  <select
                    name="year"
                    className="w-full h-12 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white"
                    autoComplete="cc-exp-year"
                    defaultValue=""
                  >
                    <option value="" disabled>Year</option>
                    {Array.from({ length: 12 }, (_, i) => {
                      const y = new Date().getFullYear() + i;
                      return <option key={y} value={y}>{y}</option>;
                    })}
                  </select>
                </div>

                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">CVV *</label>
                  <div
                    id="card-cvv"
                    className="w-full px-4 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent overflow-hidden"
                    style={{ height: '48px' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Billing Address */}
          <div className="pt-3 border-t border-gray-200 space-y-3">
            <h3 className="text-base font-semibold text-gray-900">Billing Address</h3>

            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sameAsService}
                  onChange={(e) => setSameAsService(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Billing address same as service address</span>
              </label>
            </div>

            {sameAsService ? (
              <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-800 space-y-1">
                <div className="font-semibold">Using service address:</div>
                <div>{customer.address}</div>
                <div>
                  {customer.city}, {customer.state} {customer.zipCode}
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <div>
                  <label className="block font-medium text-gray-700 mb-1">Street Address *</label>
                  <AddressAutocomplete
                    value={billingAddress.address}
                    onChange={(val) => setBillingAddress({ ...billingAddress, address: val })}
                    onSelect={(addr) => setBillingAddress({
                      ...billingAddress,
                      address: addr.street,
                      city: addr.city,
                      state: addr.state,
                      zip: addr.zipCode,
                      country: (addr.country || '').toUpperCase() === 'US' ? 'USA' : (addr.country || 'USA')
                    })}
                    placeholder="123 Main Street"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-medium text-gray-700 mb-1">City *</label>
                    <input
                      type="text"
                      required
                      value={billingAddress.city}
                      onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block font-medium text-gray-700 mb-1">State *</label>
                    <select
                      required
                      value={billingAddress.state}
                      onChange={(e) => setBillingAddress({ ...billingAddress, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {US_STATES.map((state) => (
                        <option key={state.code} value={state.code}>
                          {state.code} — {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-medium text-gray-700 mb-1">ZIP Code *</label>
                    <input
                      type="text"
                      required
                      value={billingAddress.zip}
                      onChange={(e) => setBillingAddress({ ...billingAddress, zip: e.target.value })}
                      maxLength={10}
                      placeholder="12345"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block font-medium text-gray-700 mb-1">Country *</label>
                    <select
                      required
                      value={billingAddress.country}
                      onChange={(e) => setBillingAddress({ ...billingAddress, country: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {COUNTRIES.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <input
                id="termsAccepted"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => {
                  setTermsAccepted(e.target.checked);
                  if (e.target.checked) setError('');
                }}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div className="text-sm text-gray-700">
                <label htmlFor="termsAccepted" className="font-medium cursor-pointer">
                  I agree to the Membership Terms & Conditions.
                </label>
                <div>
                  <button
                    type="button"
                    className="text-blue-700 underline font-medium hover:text-blue-800"
                    onClick={() => setIsTermsModalOpen(true)}
                  >
                    Read terms
                  </button>
                </div>

                <div className="mt-4">
                  <label htmlFor="signatureName" className="block text-sm font-medium text-gray-700">
                    Electronic signature (type your full name) *
                  </label>
                  <input
                    id="signatureName"
                    name="signatureName"
                    type="text"
                    value={signatureName}
                    onChange={(e) => {
                      setSignatureName(e.target.value);
                      if (e.target.value.trim()) setError('');
                    }}
                    placeholder={`${customer?.firstName || ''} ${customer?.lastName || ''}`.trim() || 'Full name'}
                    className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                    autoComplete="name"
                  />
                  <div className="mt-1 text-xs text-gray-600">
                    Typing your name acts as your electronic signature for these Terms & Conditions.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600 flex gap-2">
            <Shield className="h-4 w-4 text-green-600 mt-0.5" />
            <span>
              Your payment information is encrypted and processed securely by our PCI-compliant provider. We never store
              your full card number.
            </span>
          </div>
        </div>
      </div>

      <div className="text-right">
        <button
          type="button"
          onClick={() => setShowTechnicianAttribution((prev) => !prev)}
          className="text-[11px] text-gray-300 hover:text-gray-500"
        >
          Staff only
        </button>
      </div>

      {showTechnicianAttribution && (
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Technician name</label>
            <input
              type="text"
              value={technicianAttribution?.name || ''}
              onChange={(e) =>
                setTechnicianAttribution({
                  ...technicianAttribution,
                  name: e.target.value,
                })
              }
              placeholder="Name"
              autoComplete="off"
              className="w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Technician email</label>
            <input
              type="email"
              value={technicianAttribution?.email || ''}
              onChange={(e) =>
                setTechnicianAttribution({
                  ...technicianAttribution,
                  email: e.target.value,
                })
              }
              placeholder="name@company.com"
              autoComplete="off"
              className="w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
            />
          </div>
        </div>
      )}

      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          disabled={processing || loading}
          className="flex-1 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 disabled:opacity-50 text-sm"
        >
          Back to Plans
        </button>
        <button
          type="submit"
          disabled={
            processing ||
            loading ||
            !isStaxReady ||
            cardFormStatus !== 'mounted' ||
            !termsAccepted ||
            !String(signatureName || '').trim()
          }
          className="flex-1 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
        >
          {processing || loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <span>Pay ${finalPrice.toFixed(2)}</span>
          )}
        </button>
      </div>

      <p className="text-[11px] text-gray-500 text-center">
        By checking the box above, typing your name as an electronic signature, and completing this enrollment, you agree
        to our Terms & Conditions and authorize
        {membership.billingFrequency === 'monthly'
          ? ' recurring monthly charges for an annual (12-month) membership fee paid in 12 installments. Early termination may require the remaining balance, and once any benefit is used the full annual fee is earned and due.'
          : ' an annual charge each year unless you cancel before the renewal deadline.'}
      </p>

      <TermsModal isOpen={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} />
    </form>
  );
}

// Scheduling Step - Complete Implementation
function SchedulingStep({ schedule, onChange, onSubmit, onSkip, loading }: any) {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<'8am-12pm' | '12pm-4pm' | ''>('');
  const [equipmentAge, setEquipmentAge] = useState<'8_or_more' | 'under_8' | 'not_sure'>('not_sure');

  const toLocalISODate = (d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Generate next 14 days for selection
  const getNextDays = () => {
    const days = [];
    const daysToCheck = 21; // Check more days to ensure we get enough non-Sunday dates
    for (let i = 1; i <= daysToCheck && days.length < 14; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      // Skip Sundays (day 0) - no comfort club tune-ups on Sundays
      if (date.getDay() !== 0) {
        days.push(date);
      }
    }
    return days;
  };

  // Hydrate local UI state when returning to this step.
  useEffect(() => {
    if (!schedule || typeof schedule !== 'object') return;

    if (typeof schedule.date === 'string' && schedule.date && schedule.date !== selectedDate) {
      setSelectedDate(schedule.date);
    }

    if (
      (schedule.timeSlot === '8am-12pm' || schedule.timeSlot === '12pm-4pm') &&
      schedule.timeSlot !== selectedTimeSlot
    ) {
      setSelectedTimeSlot(schedule.timeSlot);
    }

    if (
      (schedule.equipmentAge === '8_or_more' ||
        schedule.equipmentAge === 'under_8' ||
        schedule.equipmentAge === 'not_sure') &&
      schedule.equipmentAge !== equipmentAge
    ) {
      setEquipmentAge(schedule.equipmentAge);
    }
  }, [schedule, selectedDate, selectedTimeSlot, equipmentAge]);

  // UX: preselect the first available date (tomorrow) so arrival-window options are immediately visible.
  useEffect(() => {
    if (selectedDate) return;
    const days = getNextDays();
    if (!days.length) return;
    setSelectedDate(toLocalISODate(days[0]));
  }, [selectedDate]);

  const handleSchedule = () => {
    const next = {
      wantsScheduling: true,
      date: selectedDate,
      timeSlot: selectedTimeSlot,
      equipmentAge,
    };
    onChange(next);
    onSubmit(next);
  };

  const handleSkipScheduling = () => {
    const next = { wantsScheduling: false };
    onChange(next);
    onSkip();
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
          <Calendar className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-3xl font-bold mb-2">Schedule Your First Preventive Maintenance</h2>
        <p className="text-gray-600">
          Let's get your system serviced! Pick a preferred day and arrival window below.
        </p>
      </div>

      <div className="bg-blue-50 p-6 rounded-lg">
        <div className="flex items-start space-x-3">
          <Check className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Your Membership Includes:</h3>
            <p className="text-blue-800">
              Professional HVAC tune-up, system inspection, and performance optimization.
              Our technicians will ensure your system is running at peak efficiency!
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Select Your Preferred Date
          </label>
          <div className="grid grid-cols-7 gap-2">
            {getNextDays().map((date) => {
              const dateStr = toLocalISODate(date);
              const isSelected = selectedDate === dateStr;
              const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNum = date.getDate();
              const monthName = date.toLocaleDateString('en-US', { month: 'short' });

              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => {
                    setSelectedDate(dateStr);
                    setSelectedTimeSlot('');
                  }}
                  className={`p-3 rounded-lg border-2 transition-all text-center ${
                    isSelected
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <div className="text-xs font-medium">{dayName}</div>
                  <div className="text-lg font-bold">{dayNum}</div>
                  <div className="text-xs">{monthName}</div>
                </button>
              );
            })}
          </div>
        </div>

        {selectedDate && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Your Preferred Arrival Window
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setSelectedTimeSlot('8am-12pm')}
                disabled={loading}
                className={`p-4 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedTimeSlot === '8am-12pm'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <div className="text-lg font-bold">8:00 AM - 12:00 PM</div>
                <div className="text-sm">Morning</div>
              </button>
              <button
                type="button"
                onClick={() => setSelectedTimeSlot('12pm-4pm')}
                disabled={loading}
                className={`p-4 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedTimeSlot === '12pm-4pm'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <div className="text-lg font-bold">12:00 PM - 4:00 PM</div>
                <div className="text-sm">Afternoon</div>
              </button>
            </div>
          </div>
        )}

        {selectedDate && selectedTimeSlot && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              About how old is your HVAC system?
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setEquipmentAge('8_or_more')}
                className={`p-3 rounded-lg border-2 transition-all text-center ${
                  equipmentAge === '8_or_more'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <div className="text-sm font-bold">8+ years</div>
                <div className="text-[11px]">Older system</div>
              </button>
              <button
                type="button"
                onClick={() => setEquipmentAge('under_8')}
                className={`p-3 rounded-lg border-2 transition-all text-center ${
                  equipmentAge === 'under_8'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <div className="text-sm font-bold">Under 8</div>
                <div className="text-[11px]">Newer system</div>
              </button>
              <button
                type="button"
                onClick={() => setEquipmentAge('not_sure')}
                className={`p-3 rounded-lg border-2 transition-all text-center ${
                  equipmentAge === 'not_sure'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <div className="text-sm font-bold">Not sure</div>
                <div className="text-[11px]">No problem</div>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              This helps us prioritize older systems.
            </p>
          </div>
        )}

        {selectedDate && selectedTimeSlot && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center space-x-2 text-green-700">
              <Check className="h-5 w-5" />
              <span className="font-semibold">
                Selected: {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })} between {selectedTimeSlot === '8am-12pm' ? '8:00 AM - 12:00 PM' : '12:00 PM - 4:00 PM'}
              </span>
            </div>
            <div className="text-sm text-green-700 mt-2">
              System age: {equipmentAge === '8_or_more' ? '8+ years' : equipmentAge === 'under_8' ? 'Under 8 years' : 'Not sure'}
            </div>
            <p className="text-sm text-green-600 mt-2">
              We'll email you a confirmation and our team will contact you with the exact appointment time.
            </p>
          </div>
        )}
      </div>

      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleSkipScheduling}
          disabled={loading}
          className="flex-1 py-4 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 disabled:opacity-50"
        >
          I'll Schedule Later
        </button>
        <button
          type="button"
          onClick={handleSchedule}
          disabled={loading || !selectedDate || !selectedTimeSlot}
          className="flex-1 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Confirming...</span>
            </>
          ) : (
            <span>Confirm Schedule</span>
          )}
        </button>
      </div>

      <p className="text-xs text-gray-500 text-center">
        Don't worry! If you skip scheduling now, you can always schedule your service visit later through your member portal or by calling us.
      </p>
    </div>
  );
}

// Complete Step - Placeholder
function CompleteStep({
  customer,
  membership,
  enrollment,
  schedule,
  scheduledVisit,
  confirmationEmail,
  errorMessage,
}: any) {
  const humanWindow = (slot: string | undefined) => {
    if (slot === '8am-12pm') return '8:00 AM - 12:00 PM';
    if (slot === '12pm-4pm') return '12:00 PM - 4:00 PM';
    return slot || '';
  };

  return (
    <div className="text-center py-12">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
        <Check className="h-10 w-10 text-green-600" />
      </div>
      <h2 className="text-3xl font-bold mb-4">Welcome to the Comfort Club!</h2>
      <p className="text-gray-700 mb-6">
        Thank you {customer.firstName}! Your membership is now active.
      </p>

      <div className="max-w-xl mx-auto text-left bg-white border border-gray-200 rounded-xl p-6 space-y-4">
        <div>
          <div className="text-sm text-gray-500">Plan</div>
          <div className="font-semibold text-gray-900">{membership?.planName || membership?.plan_name || membership?.planCode}</div>
          <div className="text-sm text-gray-600">Billing: {membership?.billingFrequency || membership?.billing_frequency}</div>
          {enrollment?.nextBillingDate && (
            <div className="text-sm text-gray-600">Next billing date: {enrollment.nextBillingDate}</div>
          )}
        </div>

        {schedule?.wantsScheduling ? (
          <div>
            <div className="text-sm text-gray-500">Maintenance scheduling</div>
            <div className="text-gray-900 font-medium">
              Requested: {schedule?.date} ({humanWindow(schedule?.timeSlot)})
            </div>
            {(scheduledVisit?.job_number || scheduledVisit?.successware_job_number) && (
              <div className="text-sm text-gray-600">
                Appointment/job #: {scheduledVisit.job_number || scheduledVisit.successware_job_number}
              </div>
            )}
            <div className="text-sm text-gray-600 mt-1">
              Need to make a change? Call our office and we’ll help you reschedule.
            </div>
          </div>
        ) : (
          <div>
            <div className="text-sm text-gray-500">Maintenance scheduling</div>
            <div className="text-gray-700">
              No problem — you can schedule later.
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            {errorMessage}
          </div>
        )}

        {typeof customer?.email === 'string' && customer.email.trim() ? (
          confirmationEmail?.attempted ? (
            confirmationEmail.sent ? (
              <div className="text-sm text-gray-600">
                Confirmation/receipt email sent to <span className="font-medium">{customer.email}</span>.
              </div>
            ) : (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                We couldn’t send a confirmation email to{' '}
                <span className="font-medium">{customer.email}</span>.
                {confirmationEmail.message ? ` ${String(confirmationEmail.message).slice(0, 160)}` : ''}
              </div>
            )
          ) : (
            <div className="text-sm text-gray-600">
              We’ll send a confirmation/receipt email to <span className="font-medium">{customer.email}</span>.
            </div>
          )
        ) : null}

        <div className="pt-2 flex flex-col sm:flex-row gap-3">
          <a
            href={`/portal?${new URLSearchParams({
              email: String(customer?.email || ''),
              phone: String(customer?.phone || ''),
              autologin: '1',
            }).toString()}`}
            target="_blank"
            rel="noreferrer"
            className="flex-1 text-center py-3 px-4 rounded-lg bg-gray-900 text-white font-semibold hover:bg-black transition-colors"
          >
            Go to Member Portal
          </a>
          <a
            href="/"
            className="flex-1 text-center py-3 px-4 rounded-lg bg-gray-100 text-gray-800 font-semibold hover:bg-gray-200 transition-colors"
          >
            Back to Home
          </a>
        </div>

        <div className="text-xs text-gray-500">
          Portal access is via your email + phone number.
        </div>
      </div>
    </div>
  );
}